import { memo, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import ApiServicePerformanceLoader from './ApiServicePerformanceLoader';
import { buildChartYAxisTicks, truncateChartLabel } from './apiServicePerformanceUtils';

const CHART_HEIGHT = 280;
const BAR_WIDTH = 56;
const BAR_GAP = 28;
const PADDING_LEFT = 48;
const PADDING_BOTTOM = 72;
const PADDING_TOP = 24;
const PADDING_RIGHT = 16;

function ApiServiceStackedColumnChart({
  title,
  yAxisLabel,
  xAxisLabel,
  series = [],
  bars = [],
  loading = false,
  loadingMessage,
  emptyMessage,
  onBarClick,
  renderTooltip,
}) {
  const [hover, setHover] = useState(null);

  const maxTotal = useMemo(
    () => Math.max(1, ...bars.map((b) => b.total || 0)),
    [bars]
  );

  const yAxisTicks = useMemo(() => buildChartYAxisTicks(maxTotal), [maxTotal]);

  const chartWidth = Math.max(
    400,
    PADDING_LEFT + PADDING_RIGHT + bars.length * (BAR_WIDTH + BAR_GAP)
  );
  const plotHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderColor: '#d5d9e0' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          {title}
        </Typography>
        <ApiServicePerformanceLoader message={loadingMessage} minHeight={CHART_HEIGHT} />
      </Paper>
    );
  }

  if (!bars.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderColor: '#d5d9e0' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>{emptyMessage}</Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: '#d5d9e0', position: 'relative' }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>

      {series.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
          {series.map((s) => (
            <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
              <Typography variant="caption" color="text.secondary">
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <svg width={chartWidth} height={CHART_HEIGHT} role="img" aria-label={title}>
          <text
            x={12}
            y={PADDING_TOP + plotHeight / 2}
            transform={`rotate(-90 12 ${PADDING_TOP + plotHeight / 2})`}
            textAnchor="middle"
            fontSize={11}
            fill="#5f6878"
          >
            {yAxisLabel}
          </text>

          {yAxisTicks.map((val) => {
            const tick = maxTotal > 0 ? val / maxTotal : 0;
            const y = PADDING_TOP + plotHeight * (1 - tick);
            return (
              <g key={val}>
                <line
                  x1={PADDING_LEFT}
                  y1={y}
                  x2={chartWidth - PADDING_RIGHT}
                  y2={y}
                  stroke="#e8eaef"
                  strokeDasharray={val === 0 ? undefined : '4 4'}
                />
                <text x={PADDING_LEFT - 8} y={y + 4} textAnchor="end" fontSize={10} fill="#888">
                  {val}
                </text>
              </g>
            );
          })}

          {bars.map((bar, index) => {
            const x = PADDING_LEFT + index * (BAR_WIDTH + BAR_GAP);
            let yCursor = PADDING_TOP + plotHeight;
            const segments = series.map((s) => ({
              series: s,
              value: Number(bar.segments?.[s.key]) || 0,
            }));

            return (
              <g key={bar.id}>
                {segments.map(({ series: s, value }) => {
                  if (!value) return null;
                  const h = (value / maxTotal) * plotHeight;
                  yCursor -= h;
                  return (
                    <rect
                      key={s.key}
                      x={x}
                      y={yCursor}
                      width={BAR_WIDTH}
                      height={h}
                      fill={s.color}
                      stroke="#fff"
                      strokeWidth={0.5}
                      onMouseEnter={() => setHover({ bar, series: s, value, x, y: yCursor })}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => onBarClick?.(bar)}
                      style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                    />
                  );
                })}
                <text
                  x={x + BAR_WIDTH / 2}
                  y={CHART_HEIGHT - 28}
                  textAnchor="end"
                  fontSize={10}
                  fill="#333"
                  transform={`rotate(-35 ${x + BAR_WIDTH / 2} ${CHART_HEIGHT - 28})`}
                  style={{ cursor: onBarClick ? 'pointer' : 'default' }}
                  onClick={() => onBarClick?.(bar)}
                >
                  {truncateChartLabel(bar.label)}
                </text>
              </g>
            );
          })}

          <text
            x={PADDING_LEFT + (chartWidth - PADDING_LEFT - PADDING_RIGHT) / 2}
            y={CHART_HEIGHT - 6}
            textAnchor="middle"
            fontSize={11}
            fill="#5f6878"
          >
            {xAxisLabel}
          </text>
        </svg>
      </Box>

      {hover && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: 72,
            left: Math.min(hover.x + 80, chartWidth - 180),
            px: 1.5,
            py: 1,
            bgcolor: '#2f3640',
            color: '#fff',
            fontSize: 12,
            pointerEvents: 'none',
            zIndex: 2,
            maxWidth: 220,
          }}
        >
          {renderTooltip ? (
            renderTooltip(hover)
          ) : (
            <>
              <div>
                <strong>{hover.bar.label}</strong>
              </div>
              <div>
                {hover.series.tooltipLabel || hover.series.label}: {hover.value}
              </div>
              <div>Total: {hover.bar.total}</div>
            </>
          )}
        </Paper>
      )}
    </Paper>
  );
}

export default memo(ApiServiceStackedColumnChart);
